import { useState, useEffect } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useLanguage } from "@/contexts/LanguageContext";

type CookieConsentProps = {
  onConsentReady?: (ready: boolean) => void;
};

const CookieConsent = ({ onConsentReady }: CookieConsentProps) => {
  const [consent, setConsent, isLoading] = useLocalStorage<string | null>("cookie-consent", null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Aguardar o carregamento do localStorage antes de decidir mostrar
    if (isLoading) return;

    if (!consent) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [consent, isLoading]);

  useEffect(() => {
    onConsentReady?.(!isLoading && Boolean(consent));
  }, [consent, isLoading, onConsentReady]);

  const handleAccept = () => {
    setConsent("accepted");
    setIsVisible(false);
    document.body.style.overflow = 'unset';
  };

  // Não mostrar nada enquanto carrega ou se já aceitou
  if (isLoading || !isVisible) return null;

  return (
    <>
      {/* Overlay bloqueante */}
      <div className="fixed inset-0 bg-black/70 z-[9998]" />
      
      {/* Modal centralizado */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90%] max-w-lg">
        <Card className="shadow-2xl border-0 bg-card">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3 text-lg">
                  {t("cookie.title")}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("cookie.description")} {t("cookie.consent_prompt")}{" "}
                  <a href="/cookies" className="text-primary underline hover:no-underline">
                    {t("cookie.policy_link")}
                  </a>.
                </p>
              </div>
              <Button
                size="lg"
                onClick={handleAccept}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {t("cookie.accept")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default CookieConsent;